import { Box, Pagination, Skeleton, Stack } from "@mui/material";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useState } from "react";
import { ArticleCard } from "./article-card";
import { useFetchArticles } from "./fetch-hooks";

interface ArticlesListProps {
  limit?: number; // If provided, only show this many articles (no pagination)
  onArticleClick?: (identifier: string) => void;
}

export const ArticlesList = ({ limit, onArticleClick }: ArticlesListProps) => {
  const [page, setPage] = useState(1);
  const pageSize = limit || 25;

  const articlesQuery = useFetchArticles(page, pageSize);

  const isLoading = articlesQuery.isPending;
  const isError = articlesQuery.isError;
  const isSuccess = articlesQuery.isSuccess;

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Stack direction="column" gap={2} sx={{ width: "100%" }}>
      <ReactQueryErrorWrapper queries={[articlesQuery]} mutations={[]} />

      {/* Loading Skeletons */}
      {isLoading &&
        Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height="234px"
            sx={{ borderRadius: "16px" }}
          />
        ))}

      {/* Articles */}
      {isSuccess && (
        <>
          <Stack direction="column" gap={2}>
            {articlesQuery.data.data.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                onClick={
                  onArticleClick
                    ? () => onArticleClick(article.slug || article.documentId)
                    : undefined
                }
              />
            ))}
          </Stack>

          {/* Pagination - only show if not limited */}
          {!limit && articlesQuery.data.meta.pagination.pageCount > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "24px",
              }}
            >
              <Pagination
                count={articlesQuery.data.meta.pagination.pageCount}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};
